import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Alert,
  Grid,
  TextField,
  InputAdornment,
  Button,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { useSnackbar } from '../Stateless/SnackBarProvider';
import SearchIcon from '@mui/icons-material/Search';
import { GridRowId } from '@mui/x-data-grid';
import CommonDataGrid from '../Stateless/CommonDataGrid';
import ErrorHandler from '../Stateless/Common/ErrorHandler';
import PermissionActionButtons from '../Stateless/Common/PermissionActionButtons';
import { usePermissions } from '../../context/PermissionContext';
import { atom } from 'jotai';
import {
  useBlockUnblockCustomerById,
  useDeleteCustomerById,
  useGetAllCustomer,
} from '../Query/CustomerQuery';
import { Add } from '@mui/icons-material';
import { categoryStyles } from '../Styles/CategoryFormStyles';
import { useManageCustomer } from '../Stateless/ManageCustomer/useManageCustomer';
import { useDebounce } from './Utils/useDounce';
// import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

type Customer = {
  id: string;
  customerName: string;
  customerEmail: string;
  status: string | 'Active' | 'Blocked';
  addedBy: string;
  customerMobileNumber: string;
  customerAlternativeMobileNumber: string;
  profileImage?: string;
  customerAddress?: string;
  latitude?: string;
  longitude?: string;
  userAddress?: {
    address: string;
    latitude: string;
    longitude: string;
    label: string;
    isDefault: boolean;
  };
  userAddresses?: Array<{
    address: string;
    latitude: string;
    longitude: string;
    label: string;
    isDefault: boolean;
  }>;
};

type EditCustomerData = {
  id: string;
  name: string;
  email: string;
  mobileNumber: string;
  alternativeMobileNumber: string;
  userAddress?: {
    address: string;
    latitude: string;
    longitude: string;
  };
  customerAddress?: string;
  profileImage?: string;
};

type CustomerListProps = {
  editCategory: (customer: Customer) => void;
  addCustomer: () => void;
  modal: boolean;
};
export const categoryStoreAtom = atom<string | null>(null);
export const customerAtom = atom<EditCustomerData | null>(null);

const CustomerList: React.FC<CustomerListProps> = ({
  // editCategory,
  addCustomer,
}) => {
  const { showMessage } = useSnackbar();
  const { hasPermission } = usePermissions();
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [snackbarMessage] = useState<string>('');
  const [snackbarSeverity] = useState<'success' | 'error' | 'info' | 'warning'>('info');

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 400);

  const { params, dispatch } = useManageCustomer();
  const isReady = Boolean(params && Object.keys(params).length > 0);
  const memoizedParams = useMemo(() => {
    return isReady ? params : {};
  }, [isReady, params]);
  const { data: customerList, isLoading, refetch } = useGetAllCustomer(memoizedParams);

  const filteredRows = useMemo(() => {
    if (!customerList?.data) return [];
    const storeOwners = customerList?.data || []
    return storeOwners.map((row : any, index: any)  => {
      // Find the default address from userAddresses array
      const defaultAddress = row.userAddresses?.find((addr: any) => addr.isDefault) || row.userAddresses?.[0] || null;
      
      return {
        ...row,
        id: row.userId,
        serialNumber: index + 1 + (Number(params.page - 1)) * Number(params.limit),
        customerName: row.name,
        customerEmail: row.email,
        mobileNumber: row.mobileNumber,
        alternativeMobileNumber: row.alternativeMobileNumber,
        userAddress: defaultAddress,
        userAddresses: row.userAddresses || [],
        profileImage: row.profileImage || null,
      };
    });
  }, [params.search, customerList, params.page, params.limit])

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
  };

  useEffect(() => {
    dispatch({ type: 'SET_SEARCH', payload: debouncedSearch ? debouncedSearch : undefined });
  }, [debouncedSearch]);

  const handlePageChange = (newPage: number) => {
    dispatch({ type: 'SET_PAGE', payload: newPage + 1 });
  };

  const handlePageSizeChange = (newSize: number) => {
    dispatch({ type: 'SET_LIMIT', payload: newSize });
  };

  // const setCategoryStoreAtom = useSetAtom(categoryStoreAtom);
  // const setCustomerAtom = useSetAtom(customerAtom);
  const deletCustomerById = useDeleteCustomerById();
  const { mutateAsync: blockStoreCustomerAsync } =
    useBlockUnblockCustomerById();


  // const handleEditCategory = (customer: Customer) => {
  //   editCategory(customer);
  // };

  const handleBlock = async () => {
    if (selectedCategoryIds.length === 0) {
      showMessage('No customer selected to block', 'error');
      return;
    }

    if (
      window.confirm(
        `Are you sure you want to block ${selectedCategoryIds.length} Customer(s)?`
      )
    ) {
      try {
        await blockStoreCustomerAsync({
          id: selectedCategoryIds,
          data: { status: 'blocked' },
        });
        refetch()
        showMessage('Customer blocked successfully', 'success');
      } catch {
        showMessage('Failed to block customers', 'error');
      }
    }
  };

  const handleUnblock = async () => {
    if (selectedCategoryIds.length === 0) {
      showMessage('No customer selected to unblock', 'error');
      return;
    }

    if (
      window.confirm(
        `Are you sure you want to unblock ${selectedCategoryIds.length} Customer(s)?`
      )
    ) {
      try {
        await blockStoreCustomerAsync({
          id: selectedCategoryIds,
          data: { status: 'unblocked' },
        });
        refetch()
        showMessage('Customer unblocked successfully', 'success');
      } catch {
        showMessage('Failed to unblock customers', 'error');
      }
    }
  };

  const handleDelete = (ids: string[]) => {
    if (ids.length === 0) {
      showMessage('No categories selected to delete', 'error');
      return;
    }

    if (
      window.confirm(
        `Are you sure you want to delete ${ids.length} category(s)?`
      )
    ) {
      ids.forEach(() => {
        deletCustomerById.mutate(ids, {
          onSuccess: () => {
            setSelectedCategoryIds([]);
            refetch()
          },
          onError: (error: any) => {
            showMessage(error?.message || 'Failed to delete category', 'error');
          },
        });
      });

      showMessage(`${ids.length} category(s) deleted successfully`, 'success');
    }
  };

  const handleDeleteSelected = () => {
    if (selectedCategoryIds.length === 0) {
      showMessage('No Customer selected to delete', 'error');
      return;
    }

    if (
      window.confirm(
        `Are you sure you want to delete ${selectedCategoryIds.length} customer(s)?`
      )
    ) {
      deletCustomerById.mutate(selectedCategoryIds, {
        onSuccess: () => {
          setSelectedCategoryIds([]);
          refetch()
        },
        onError: (error: any) => {
          showMessage(error?.message || 'Failed to delete customer', 'error');
        },
      });

      showMessage(
        `${selectedCategoryIds.length} customer(s) deleted successfully`,
        'success'
      );
    }
  };

  // Determine which bulk buttons to show based on selected rows' statuses
  const { showBlock, showUnblock } = useMemo(() => {
    if (!selectedCategoryIds || selectedCategoryIds.length === 0) return { showBlock: false, showUnblock: false };
    const dataList = customerList?.data || [];
    const idToStatus = new Map<string, string>(
      dataList.map((item: any) => [String(item.userId), String(item.status || '')])
    );
    const statuses = selectedCategoryIds.map((id) => idToStatus.get(String(id)) || '');
    const hasBlocked = statuses.some((s) => s === 'blocked');
    const hasUnblocked = statuses.some((s) => s === 'unblocked' || s === 'active');
    return {
      showBlock: hasUnblocked,
      showUnblock: hasBlocked,
    };
  }, [selectedCategoryIds, customerList]);

  const RowActions: React.FC<{ row: any }> = ({ row }) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const canEdit = hasPermission('customer', 'Edit');
    const canDelete = hasPermission('customer', 'Delete');
    if (!canEdit && !canDelete) return null;
    const id = typeof row.id === 'string' ? row.id : '';
    return (
      <>
        <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)}>
          <MoreVertIcon fontSize="small" />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          {/* {canEdit && (
            <MenuItem onClick={() => { setAnchorEl(null); handleEditCategory(row); }}>
              <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Edit</ListItemText>
            </MenuItem>
          )} */}
          {canDelete && (
            <MenuItem onClick={() => { setAnchorEl(null); if (id) handleDelete([id]); }}>
              <ListItemIcon><DeleteIcon fontSize="small" /></ListItemIcon>
              <ListItemText sx={{ color: 'error.main' }}>Delete</ListItemText>
            </MenuItem>
          )}
        </Menu>
      </>
    );
  };

  const columns = [
    { field: 'serialNumber', headerName: 'S.No', width: 90 },
    { field: 'customerName', headerName: 'Customer Name', flex: 1 },
    { field: 'customerEmail', headerName: 'Customer Email', flex: 1 },
    { field: 'addedBy', headerName: 'Added By', width: 120 },
    {
      field: 'status',
      headerName: 'Status',
      width: 110,
      filterable: false,
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params: any) => {
        const isBlocked = params.value === 'blocked' ? true : false;
        const id = params.row.id as string;
        const canBlock = hasPermission('customer', 'Block');
        const canUnblock = hasPermission('customer', 'Unblock');
        if ((isBlocked && !canUnblock) || (!isBlocked && !canBlock)) return null;
        return (
          <IconButton
            size="small"
            sx={{ color: isBlocked ? '#f44336' : '#4caf50' }}
            onClick={() =>
              isBlocked
                ? blockStoreCustomerAsync({ id: [id], data: { status: 'unblocked' } })
                : blockStoreCustomerAsync({ id: [id], data: { status: 'blocked' } })
            }
          >
            {isBlocked ? (
              <BlockIcon fontSize="small" />
            ) : (
              <CheckCircleIcon fontSize="small" />
            )}
          </IconButton>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      filterable: false,
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params: any) => <RowActions row={params.row} />,
    },
  ];

  const handleCategorySelection = (selectionModel: GridRowId[]) => {
    const ids = selectionModel.map((id) => String(id));
    setSelectedCategoryIds(ids);
  };

  return (
    <Box sx={categoryStyles.ListContainer}>
      {snackbarOpen && (
        <Box sx={{ mb: 2 }}>
          <Alert
            severity={snackbarSeverity}
            onClose={() => setSnackbarOpen(false)}
          >
            {snackbarMessage}
          </Alert>
        </Box>
      )}

      <Grid
        container
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
        wrap="wrap"
        sx={{ mb: 2 }}
      >
        <Box
          sx={{
            width: 600,
            border: '1px solid #ccc',
            borderRadius: '8px',
            background: '#fff',
          }}
        >
          <TextField
            fullWidth
            size="medium"
            placeholder="Search by Customer name"
            value={searchTerm}
            onChange={handleSearchChange}
            sx={{
              '.MuiOutlinedInput-root': {
                height: 40,
                width: 600,
                border: 'none', // remove inner border
              },
              '& fieldset': {
                border: 'none', // remove Material-UI default border
              },
              '& .MuiInputBase-input': {
                paddingTop: '10px', // adjust this value to move the placeholder lower
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 20, color: '#aaa', mt: '4px' }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Grid
          container
          justifyContent="flex-end"
          spacing={1}
          alignItems="center"
        >
          <Grid>
            <PermissionActionButtons
              module="customer"
              onBlock={showBlock ? handleBlock : undefined}
              onUnblock={showUnblock ? handleUnblock : undefined}
              onDelete={selectedCategoryIds.length ? handleDeleteSelected : undefined}
            />
          </Grid>
          <Grid>
            {hasPermission('customer', 'Add') && (
              <Button
                onClick={() => addCustomer()}
                variant="contained"
                color="primary"
                size="small"
                sx={{
                  textTransform: 'none',
                  borderRadius: 2,
                  boxShadow: 'none',
                  '&:hover': {
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                  },
                }}
                startIcon={<Add sx={{ fontSize: 16}} />}
              >
                Add Customer
              </Button>
            )}
          </Grid>
        </Grid>
      </Grid>

      <CommonDataGrid
        key={`datagrid-page-${params.page}-limit-${params.limit}`}
        rows={filteredRows || []}
        columns={columns}
        pageSize={params.limit}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        checkboxSelection
        loading={isLoading}
        page={params.page - 1}
        rowCount={customerList?.pagination?.totalItems}
        components={{
          NoRowsOverlay: () => <ErrorHandler type="no-data" message="No categories found" onRetry={() => refetch()} />
        }}
        onRowSelectionModelChange={handleCategorySelection}
        selectionModel={selectedCategoryIds}
      />

      {/* {expanded && selectedCategory && (
       
      )} */}
    </Box>
  );
};

export default CustomerList;
